const CARD_GLYPHS = {
  feedback: 'pulse',
  meals: 'meal',
  messages: 'message',
  program: 'program',
  review: 'calendar',
};

function SummaryGlyph({ kind }) {
  const common = {
    width: 18,
    height: 18,
    viewBox: '0 0 20 20',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.55,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
  };

  switch (kind) {
    case 'meal':
      return <svg {...common}><path d="M5 3.2v5.2M2.9 3.2v3.1A2.1 2.1 0 0 0 5 8.4a2.1 2.1 0 0 0 2.1-2.1V3.2M5 8.4v8.4M13.7 3.2v13.6M13.7 3.2c2.1 1.2 3.1 3.1 3.1 5.7h-3.1" /></svg>;
    case 'message':
      return <svg {...common}><path d="M3.1 4.2h13.8v9.2H8l-4.9 3v-12.2Z" /><path d="M6.2 8.8h7.6" /></svg>;
    case 'program':
      return <svg {...common}><path d="M5.1 2.8h8.2a1.7 1.7 0 0 1 1.7 1.7v12.7H6.7A1.7 1.7 0 0 1 5 15.5V4.6a1.8 1.8 0 0 1 1.8-1.8" /><path d="M5 14.2h10M8 6.4h4.2M8 9.3h4.8" /></svg>;
    case 'calendar':
      return <svg {...common}><rect x="3" y="4.3" width="14" height="12.2" rx="2" /><path d="M6.5 2.8v3M13.5 2.8v3M3 8h14M6.5 11.1h.1M10 11.1h.1M13.5 11.1h.1" /></svg>;
    default:
      return <svg {...common}><path d="M2.8 10h3l1.6-4.2 3.1 8.4 2-5.1 1.2 2.2h3.5" /></svg>;
  }
}

function ArrowGlyph() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 7h8M8 4l3 3-3 3" />
    </svg>
  );
}

function formatPeriod(period) {
  const from = Date.parse(period?.from);
  const to = Date.parse(period?.to);
  if (!Number.isFinite(from) || !Number.isFinite(to)) return null;

  const format = (timestamp, includeYear = false) => new Date(timestamp).toLocaleDateString('fr-CH', {
    day: 'numeric',
    month: 'short',
    ...(includeYear ? { year: 'numeric' } : {}),
  });
  const sameYear = new Date(from).getFullYear() === new Date(to).getFullYear();
  return `${format(from, !sameYear)} – ${format(to, true)}`;
}

function SummaryCard({ card, onActivate }) {
  const unavailable = card?.status === 'unavailable';
  const className = [
    'jrn-longitudinal__card',
    unavailable ? 'jrn-longitudinal__card--unavailable' : '',
    onActivate ? 'jrn-longitudinal__card--action' : '',
  ].filter(Boolean).join(' ');
  const content = (
    <>
      <span className="jrn-longitudinal__card-icon">
        <SummaryGlyph kind={CARD_GLYPHS[card?.id] || 'pulse'} />
      </span>
      <span className="jrn-longitudinal__card-copy">
        <span className="jrn-longitudinal__card-label">{card?.label || 'Information'}</span>
        <strong className="jrn-longitudinal__card-value">{card?.value || 'Non renseigné'}</strong>
        {card?.detail && <span className="jrn-longitudinal__card-detail">{card.detail}</span>}
      </span>
      {onActivate && <span className="jrn-longitudinal__card-arrow"><ArrowGlyph /></span>}
    </>
  );

  if (onActivate) {
    return (
      <button type="button" className={className} onClick={onActivate} aria-label={`Ouvrir : ${card?.label || 'information'}`}>
        {content}
      </button>
    );
  }
  return <div className={className}>{content}</div>;
}

/**
 * Synthèse de présentation pure du suivi longitudinal.
 * Toutes les valeurs et les éventuels points à reprendre sont calculés en amont.
 */
export default function LongitudinalFollowupSummary({
  summary = null,
  status = 'ok',
  errorMessage = null,
  onRetry = null,
  onOpenMessages = null,
  onOpenProgram = null,
}) {
  if (status === 'loading') {
    return (
      <section className="jrn-longitudinal jrn-longitudinal--loading" aria-busy="true" aria-label="Chargement de la synthèse du suivi">
        <div className="jrn-longitudinal__skeleton jrn-longitudinal__skeleton--title" />
        <div className="jrn-longitudinal__skeleton-grid" aria-hidden="true">
          {[0, 1, 2, 3].map((item) => <div key={item} className="jrn-longitudinal__skeleton jrn-longitudinal__skeleton--card" />)}
        </div>
      </section>
    );
  }

  if (status === 'error' || status === 'unavailable') {
    const unavailable = status === 'unavailable';
    return (
      <section className={`jrn-longitudinal jrn-longitudinal--${status}`} aria-live="polite">
        <div className="jrn-longitudinal__state-mark" aria-hidden="true">{unavailable ? '—' : '!'}</div>
        <div>
          <p className="jrn-longitudinal__eyebrow">Lecture du suivi</p>
          <h3 className="jrn-longitudinal__state-title">
            {unavailable ? 'Suivi application non disponible' : 'La synthèse n’est pas disponible'}
          </h3>
          <p className="jrn-longitudinal__state-copy">
            {unavailable
              ? 'Cette cliente ne dispose pas encore d’un historique dans l’application.'
              : (errorMessage || 'Les données n’ont pas pu être réunies pour le moment.')}
          </p>
        </div>
        {!unavailable && typeof onRetry === 'function' && (
          <button type="button" className="jrn-longitudinal__retry" onClick={onRetry}>Réessayer</button>
        )}
      </section>
    );
  }

  const cards = Array.isArray(summary?.cards) ? summary.cards : [];
  const attention = Array.isArray(summary?.attention) ? summary.attention : [];
  const period = formatPeriod(summary?.period);
  const title = summary?.anchor?.label || 'Depuis le dernier point';

  return (
    <section className="jrn-longitudinal" aria-labelledby="longitudinal-followup-title">
      <header className="jrn-longitudinal__header">
        <div className="jrn-longitudinal__heading">
          <span className="jrn-longitudinal__accent" aria-hidden="true" />
          <div>
            <p className="jrn-longitudinal__eyebrow">Lecture du suivi</p>
            <h3 id="longitudinal-followup-title" className="jrn-longitudinal__title">{title}</h3>
          </div>
        </div>
        {(period || summary?.durationDays) && (
          <p className="jrn-longitudinal__period">
            {period}
            {summary?.durationDays ? <span>{summary.durationDays} jour{summary.durationDays > 1 ? 's' : ''}</span> : null}
          </p>
        )}
      </header>

      {cards.length > 0 ? (
        <div className="jrn-longitudinal__grid">
          {cards.map((card, index) => {
            const onActivate = card?.id === 'messages' && typeof onOpenMessages === 'function'
              ? onOpenMessages
              : (card?.id === 'program' && typeof onOpenProgram === 'function' ? onOpenProgram : null);
            return <SummaryCard key={card?.id || `summary-card-${index}`} card={card} onActivate={onActivate} />;
          })}
        </div>
      ) : (
        <p className="jrn-longitudinal__empty">Aucune donnée consolidée sur cette période.</p>
      )}

      {attention.length > 0 && (
        <div className="jrn-longitudinal__attention">
          <span className="jrn-longitudinal__attention-label">Faits de la période</span>
          <ul>
            {attention.map((item, index) => <li key={`${item}-${index}`}>{item}</li>)}
          </ul>
        </div>
      )}
    </section>
  );
}
